import yaml from "js-yaml";
import type { Recommendation, CollectsField } from "@shared";
import { recommendationEntry } from "@shared";

/**
 * The recommendations registry, bundled into the frontend at build time.
 *
 * No ranker, no state channel: the chips row always renders every entry
 * available on the current surface, straight from the registry markdown
 * (shared/recommendations — Vite globs it across the monorepo; edits
 * hot-reload in dev; shared/ is the one sibling directory present in the
 * production Rails image at asset-precompile time). The backend's only
 * involvement is resolving a CLICK (`resolveRecommendation` reads
 * `recommendation_id` and preloads the SOP); which chips exist is a static
 * fact of the deploy, so the frontend owns it. This kills the per-turn
 * Haiku ranking call and the empty-channel failure mode (old threads,
 * skipped turns → no chips).
 */

const recommendationFiles = import.meta.glob(
  "../../../../../shared/recommendations/**/*.md",
  { query: "?raw", import: "default", eager: true },
) as Record<string, string>;

export interface RegistryEntry extends Recommendation {
  surface: string;
  status: "available" | "partial" | "needs-build";
  /** Pages within a surface (ads substeps); absent = anywhere on it. */
  stages?: string[];
  availability: string[];
}

function parseFrontmatter(raw: string): Record<string, unknown> | null {
  const match = raw.match(/^---\n([\s\S]*?)\n---/);
  if (!match) return null;
  try {
    return yaml.load(match[1]) as Record<string, unknown>;
  } catch {
    return null; // malformed entry — CI validates; never break the chat over it
  }
}

// Validate against the SAME zod contract the backend loader uses
// (shared/recommendations/schema.ts) — one source of truth, no strict-vs-loose
// divergence. Invalid files are dropped here (the chat never breaks over one),
// and surfaced via `invalidRegistryFiles` so a test fails loudly before ship.
const parsedFiles = Object.entries(recommendationFiles).map(([path, raw]) => {
  const id = path.replace(/^.*\/recommendations\//, "").replace(/\.md$/, "");
  const fm = parseFrontmatter(raw);
  return { id, result: fm == null ? null : recommendationEntry.safeParse(fm) };
});

/**
 * Files whose frontmatter fails the shared schema. The backend's zod loader
 * THROWS on these (taking down every click's resolution); the frontend used to
 * parse them loosely and ship them silently. Exposed so a frontend test fails
 * loudly by name — the two loaders now agree on what "valid" means.
 */
export const invalidRegistryFiles: ReadonlyArray<{ id: string; issues: string[] }> =
  parsedFiles
    .filter((p) => !p.result || !p.result.success)
    .map((p) => ({
      id: p.id,
      issues:
        p.result && !p.result.success
          ? p.result.error.issues.map((i) => i.message)
          : ["unparseable frontmatter"],
    }));

const registry: RegistryEntry[] = parsedFiles
  .flatMap(({ id, result }): RegistryEntry[] => {
    if (!result || !result.success) return [];
    const e = result.data;
    return [
      {
        recommendationId: id,
        surface: id.split("/")[0] ?? "",
        text: e.suggested_text,
        prompt: e.prompt,
        description: e.description,
        component: e.component ?? null,
        icon: e.icon ?? null,
        priority: e.priority,
        collects: e.collects as (string | CollectsField)[] | undefined,
        status: e.status,
        stages: e.stages,
        availability: e.availability,
      },
    ];
  })
  .sort((a, b) => a.recommendationId.localeCompare(b.recommendationId));

/** Every valid parsed entry, for cross-stack tests. */
export const allRecommendations: readonly RegistryEntry[] = registry;

/**
 * Every recommendation to offer on a surface (and, when given, a page/stage
 * within it — ads substeps). Shippable entries only: `needs-build` is
 * excluded (its availability is empty by registry invariant anyway).
 */
/** One entry by id — the transcript card's lookup (icon, text, collects). */
export function recommendationById(id: string): RegistryEntry | undefined {
  return registry.find((entry) => entry.recommendationId === id);
}

export function recommendationsForSurface(
  surface: "brainstorm" | "website" | "ads" | "homepage",
  stage?: string,
): Recommendation[] {
  return registry
    .filter((e) => e.availability.includes(surface))
    .filter((e) => e.status !== "needs-build")
    .filter((e) => !stage || !e.stages || e.stages.includes(stage))
    .map(({ recommendationId, text, prompt, description, component, icon, priority, collects }) => ({
      recommendationId,
      text,
      prompt,
      description,
      component,
      icon,
      priority,
      collects,
    }))
    // Pinned favorites first (priority ascending), then short chips first
    // so flex rows pack densely — ties break alphabetically for a stable
    // order across renders. This order is what packPages consumes: it
    // grabs the earliest chip that fits each slot, so favorites lead and
    // shorter chips back-fill the gaps.
    .sort(
      (a, b) =>
        (a.priority ?? Infinity) - (b.priority ?? Infinity) ||
        a.text.length - b.text.length ||
        a.text.localeCompare(b.text),
    );
}
